<script setup lang="ts">
import type { StepperOption } from "#bb/types/blocks/content/StepperOption";
import { eventBus } from "#bb/lib/eventBus";
import { resolveNavigationTarget } from "#bb/lib/navigationTarget";

const props = defineProps<{ id: string }>();

const { getContentById } = useContentManager();
const definitionData = computed(() => getContentById(props.id) as StepperOption);


const commands = [
  {
    id: "editors-group",
    type: "group",
    name: "Editors",
    description: "Building block editors",
    commands: [
      {
        id: "edit",
        name: "Edit",
        description: "Edit the stepper",
        action: async () => {
          eventBus.emit(`component:onEdit:${props.id}`, {
            data: definitionData.value,
          });
        },
        icon: "heroicons:pencil",
      },
    ],
  },
];

// Expose the commands and properties
defineExpose({
  commands,
  properties: {
    id: props.id,
    name: "Stepper",
    icon: "lucide:circle-arrow-right",
  },
});

const route = useRoute();
const features = inject<{
  resourceDefinitionPath?: string | null;
}>("render-features", {});
const { resolveHref } = useAppRoute();
const getStepPath = (step: {
  path?: string;
  resourcePath?: string;
  resourceId?: string;
}) => {
  const rawPath = (step.path || step.resourcePath || step.resourceId || "").trim();

  if (!rawPath) {
    return route.fullPath;
  }

  return resolveNavigationTarget(rawPath, {
    currentPath: route.fullPath,
    resourceDefinitionPath: features.resourceDefinitionPath,
    resolvePath: resolveHref,
  })?.href ?? route.fullPath;
};
</script>

<template>
  <nav aria-label="Progress">
    <ol role="list" class="m-4 space-y-4 @min-[768px]:flex @min-[768px]:space-y-0 @min-[768px]:space-x-8">
      <li
        v-for="(step, n) in definitionData?.steps ?? []"
        :key="`stepper-${props.id}-step-${n}`"
        class="cursor-pointer @min-[768px]:flex-1"
      >
        <nuxt-link
          v-if="step.status === 'completed'"
          :to="getStepPath(step)"
          class="group pl-4 py-2 flex flex-col border-l-4 border-primary hover:border-primary-800 @min-[768px]:pl-0 @min-[768px]:pt-4 @min-[768px]:pb-0 @min-[768px]:border-l-0 @min-[768px]:border-t-4"
        >
          <span
            class="text-xs text-primary font-semibold tracking-wide uppercase group-hover:text-primary"
            >{{ step.title }}</span
          >
          <span class="text-sm font-medium">{{ step.description }}</span>
        </nuxt-link>
        <nuxt-link
          v-else-if="step.status === 'active'"
          :to="getStepPath(step)"
          class="pl-4 py-2 flex flex-col border-l-4 border-primary @min-[768px]:pl-0 @min-[768px]:pt-4 @min-[768px]:pb-0 @min-[768px]:border-l-0 @min-[768px]:border-t-4"
          aria-current="step"
        >
          <span
            class="text-xs text-primary font-semibold tracking-wide uppercase"
            >{{ step.title }}</span
          >
          <span class="text-sm font-medium">{{ step.description }}</span>
        </nuxt-link>
        <nuxt-link
          v-else
          :to="getStepPath(step)"
          class="group pl-4 py-2 flex flex-col border-l-4 border-gray-200 hover:border-gray-300 @min-[768px]:pl-0 @min-[768px]:pt-4 @min-[768px]:pb-0 @min-[768px]:border-l-0 @min-[768px]:border-t-4"
        >
          <span
            class="text-xs text-gray-500 font-semibold tracking-wide uppercase group-hover:text-gray-700"
            >{{ step.title }}</span
          >
          <span class="text-sm font-medium">{{ step.description }}</span>
        </nuxt-link>
      </li>
    </ol>
  </nav>
</template>

<script lang="ts">
import { ComponentDefinitionIds } from "#bb/lib/componentDefinitionIds";

export default {
  componentId: ComponentDefinitionIds.NAVIGATION_STEPPER_01,
  icon: "lucide:circle-arrow-right",
  type: "Stepper 01",
  description: "A basic multi-step navigation progress component.",
  editor: () => import("#bb/components/editor/navigation/stepper/01.vue"),
  default: {
    steps: [
      {
        id: "step-1",
        title: "Step 1",
        description: "First step",
        path: "/1",
        status: "active",
      },
      {
        id: "step-2",
        title: "Step 2",
        description: "Second step",
        path: "/2",
        status: "upcoming",
      },
    ],
  },
  categories: ["Navigation"],
  order: 160102,
};
</script>
